import React, { useState, useMemo } from 'react';
import {
  SettingsIcon,
  PackageBoxIcon,
  SignalIcon,
  TimerIcon,
  WrenchIcon,
  SaveIcon,
  ClipboardIcon,
  WarningIcon,
} from '@bizfirst/common-react';
import { ExecutionObserverConfig } from '../models/ExecutionObserverConfig';
import { BasicConfigTab } from './tabs/BasicConfigTab';
import { EntityFiltersTab } from './tabs/EntityFiltersTab';
import { EventFiltersTab } from './tabs/EventFiltersTab';
import { PerformanceFiltersTab } from './tabs/PerformanceFiltersTab';
import { AdvancedTab } from './tabs/AdvancedTab';
import { SavedConfigsTab } from './tabs/SavedConfigsTab';
import { ConsoleTab } from './tabs/ConsoleTab';
import { PreviewSection } from './PreviewSection';
import { useObserverPreferences } from '../hooks/useObserverPreferences';
import '../styles/ObserverConfigEditor.css';

type TabName = 'basic' | 'entity' | 'event' | 'performance' | 'advanced' | 'saved' | 'console';

interface ObserverConfigEditorProps {
  initialConfig?: ExecutionObserverConfig;
  onConfigChange?: (config: ExecutionObserverConfig) => void;
  readonly?: boolean;
}

/**
 * Comprehensive Observer Configuration Editor
 *
 * Provides tabbed interface for configuring:
 * - Basic settings (enable, detail level, presets)
 * - Entity type filters (Process, Thread, Node, Config, Expression, Policy)
 * - Event type filters (Starting, Executing, Completed, Errored)
 * - Performance filters (min/max duration, slow threshold)
 * - Advanced JSON editing
 * - Saved configuration management
 * - Live preview of matching events
 *
 * All changes are persisted to localStorage automatically.
 */
export const ObserverConfigEditor: React.FC<ObserverConfigEditorProps> = ({
  initialConfig,
  onConfigChange,
  readonly = false
}) => {
  const [config, setConfig] = useState<ExecutionObserverConfig>(
    initialConfig || getDefaultConfig()
  );
  const [activeTab, setActiveTab] = useState<TabName>('basic');
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { getSavedConfigs } = useObserverPreferences();
  const savedConfigs = useMemo(() => getSavedConfigs(), [getSavedConfigs]);

  // Calculate preview stats
  const previewStats = useMemo(() => {
    const totalEvents = 1000; // Placeholder - would come from actual events
    const matchingEvents = Math.floor(totalEvents * (config.enabled ? 0.7 : 0));
    return {
      totalEvents,
      matchingEvents,
      filteredOutEvents: totalEvents - matchingEvents,
      matchPercentage: (matchingEvents / totalEvents) * 100
    };
  }, [config]);

  const handleConfigChange = (newConfig: ExecutionObserverConfig) => {
    if (readonly) return;

    setConfig(newConfig);
    validateConfig(newConfig);
    onConfigChange?.(newConfig);
  };

  const validateConfig = (cfg: ExecutionObserverConfig) => {
    const errors: string[] = [];

    if (cfg.enabled) {
      // Validation rules only apply if enabled
      const perf = cfg.filter?.performance;
      if (perf) {
        if (perf.minDurationMs !== undefined && perf.minDurationMs < 0) {
          errors.push('Min duration must be >= 0');
        }
        if (perf.maxDurationMs !== undefined && perf.maxDurationMs < 0) {
          errors.push('Max duration must be >= 0');
        }
        if (
          perf.minDurationMs !== undefined &&
          perf.maxDurationMs !== undefined &&
          perf.minDurationMs > perf.maxDurationMs
        ) {
          errors.push('Min duration cannot exceed max duration');
        }
      }
    }

    setValidationErrors(errors);
    setIsValid(errors.length === 0);
  };

  const tabs: { name: TabName; label: string; icon: React.ReactNode }[] = [
    { name: 'basic', label: 'Basic', icon: <SettingsIcon size={16} /> },
    { name: 'entity', label: 'Entity Filters', icon: <PackageBoxIcon size={16} /> },
    { name: 'event', label: 'Event Types', icon: <SignalIcon size={16} /> },
    { name: 'performance', label: 'Performance', icon: <TimerIcon size={16} /> },
    { name: 'advanced', label: 'Advanced', icon: <WrenchIcon size={16} /> },
    { name: 'saved', label: 'Saved', icon: <SaveIcon size={16} /> },
    { name: 'console', label: 'Console', icon: <ClipboardIcon size={16} /> }
  ];

  return (
    <div className="observer-config-editor">
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="validation-errors">
          {validationErrors.map((error, i) => (
            <div key={i} className="error-message">
              <WarningIcon size={14} /> {error}
            </div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="observer-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.name}
            className={`tab-button ${activeTab === tab.name ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.name)}
            disabled={readonly}
            title={tab.label}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {config ? (
          <>
            {activeTab === 'basic' && (
              <BasicConfigTab
                config={config}
                onChange={handleConfigChange}
                readonly={readonly}
              />
            )}

            {activeTab === 'entity' && (
              <EntityFiltersTab
                config={config}
                onChange={handleConfigChange}
                readonly={readonly}
              />
            )}

            {activeTab === 'event' && (
              <EventFiltersTab
                config={config}
                onChange={handleConfigChange}
                readonly={readonly}
              />
            )}

            {activeTab === 'performance' && (
              <PerformanceFiltersTab
                config={config}
                onChange={handleConfigChange}
                readonly={readonly}
              />
            )}

            {activeTab === 'advanced' && (
              <AdvancedTab
                config={config}
                onChange={handleConfigChange}
                onValidate={setIsValid}
                readonly={readonly}
              />
            )}

            {activeTab === 'saved' && (
              <SavedConfigsTab
                savedConfigs={savedConfigs}
                onLoad={(cfg) => handleConfigChange(cfg.config)}
                onDelete={() => {}}
                readonly={readonly}
              />
            )}

            {activeTab === 'console' && (
              <ConsoleTab
                config={config}
                logs={[]}
                readonly={readonly}
              />
            )}
          </>
        ) : (
          <div className="loading">Initializing configuration...</div>
        )}
      </div>

      {/* Preview Section */}
      {!readonly && (
        <PreviewSection
          config={config}
          stats={previewStats}
        />
      )}
    </div>
  );
};

/**
 * Get default observer configuration
 * All filters enabled by default for comprehensive observation
 */
function getDefaultConfig(): ExecutionObserverConfig {
  return {
    enabled: true,
    detailLevel: 'detailed' as const,
    filter: {
      entityTypes: {
        includeProcess: true,
        includeThread: true,
        includeNode: true,
        includeConfigResolution: true,  // Enabled by default
        includeExpressions: true,       // Enabled by default
        includePolicies: true,          // Enabled by default
        includeGuardRails: true,        // Enabled by default
        includeRouting: true            // Enabled by default
      },
      eventTypes: {
        includeStarting: true,
        includeExecuting: true,
        includeCompleted: true,
        includePaused: true,
        includeErrored: true,
        errorsOnly: false
      }
    }
  };
}

export default ObserverConfigEditor;
